/**
 * Every keyboard shortcut, declared once.
 *
 * WHY THIS EXISTS. Bindings used to live in five files — `Play.js` held bare
 * `'KeyE'` and `'KeyM'` strings, and three components each defined their own
 * `OPEN_KEY`/`CLOSE_KEY`/`DROP_KEY`. The corner hint that listed them for
 * players was written by hand and was already wrong: it omitted Shift+M, the
 * answer keys, and both admin keys, one feature after being written.
 *
 * So handlers now import their key from here, and the legend renders from the
 * same list. Adding a shortcut is ONE entry, and a shortcut that exists but is
 * undocumented becomes impossible rather than merely unlikely.
 *
 * INPUT.JS IS DELIBERATELY NOT PART OF THIS. Movement maps eight codes onto
 * four directions as HELD state — a different shape from one-key-one-action —
 * and 16 tests pin its behaviour. It is described below for the legend without
 * being owned here; bending one shape over both would complicate the thing that
 * already works in order to serve a display concern.
 */

/**
 * @typedef {object} Shortcut
 * @property {string} id      stable handle for handlers to import by
 * @property {string} [code]  KeyboardEvent.code; absent for rows that only
 *                            describe (movement, the answer keys).
 *
 *                            NOTE for `Enter` and `Escape`: their `key` and
 *                            `code` are the same string, which is why ChatPanel
 *                            and DialoguePanel can keep comparing `event.key`.
 *                            Do not "tidy" those into `event.code` — numpad
 *                            Enter reports code `NumpadEnter` and would stop
 *                            opening chat, silently.
 * @property {boolean} [shift] requires Shift
 * @property {string} keys    what to print in the legend
 * @property {string} label   what it does
 * @property {string} group   see GROUPS
 * @property {string} [when]  the precondition, when there is one
 */

/** Legend sections, in the order they read best. */
export const GROUPS = [
  { id: 'move', title: 'Getting around' },
  { id: 'talk', title: 'Talking' },
  { id: 'map', title: 'The map' },
  { id: 'admin', title: 'Admin', adminOnly: true },
];

export const SHORTCUTS = [
  {
    id: 'move',
    keys: 'W A S D',
    label: 'Move — arrow keys work too',
    group: 'move',
    // No `code`: owned by Input.js as held state. See the note above.
  },
  {
    id: 'chatOpen',
    code: 'Enter',
    keys: 'Enter',
    label: 'Say something — only people who can see you hear it',
    group: 'talk',
  },
  {
    id: 'chatClose',
    code: 'Escape',
    keys: 'Esc',
    label: 'Back to the game',
    group: 'talk',
    when: 'while typing',
  },
  {
    id: 'talk',
    code: 'KeyE',
    keys: 'E',
    label: 'Talk to whoever you are standing next to',
    group: 'talk',
    when: 'near someone with something to say',
  },
  {
    id: 'answer',
    keys: '1 – 9',
    label: 'Pick an answer',
    group: 'talk',
    when: 'mid-conversation',
    // No `code`: DialoguePanel reads the digit itself rather than binding nine
    // separate keys.
  },
  {
    id: 'mapCorner',
    code: 'KeyM',
    keys: 'M',
    label: 'Map in the corner',
    group: 'map',
  },
  {
    id: 'mapLarge',
    code: 'KeyM',
    shift: true,
    keys: 'Shift + M',
    label: 'Map, large',
    group: 'map',
  },
  {
    id: 'legend',
    code: 'KeyL',
    keys: 'L',
    label: 'This list',
    group: 'map',
  },
  {
    id: 'dropWaypoint',
    code: 'KeyR',
    keys: 'R',
    label: 'Drop a waypoint where you stand',
    group: 'admin',
    when: 'while recording a route',
  },
  {
    id: 'undoWaypoint',
    code: 'KeyU',
    keys: 'U',
    label: 'Undo the last waypoint',
    group: 'admin',
    when: 'while recording a route',
  },
];

/** Look one up by id. Throws rather than returning undefined, because a typo
 *  here would silently unbind a key and that is very hard to notice. */
export function shortcut(id) {
  const found = SHORTCUTS.find((s) => s.id === id);
  if (!found) throw new Error(`Unknown shortcut "${id}"`);
  return found;
}

/** The `KeyboardEvent.code` a handler should compare against. */
export const codeOf = (id) => shortcut(id).code;

/**
 * Does this event match the shortcut, Shift included?
 *
 * Shift is checked in BOTH directions on purpose: `M` and `Shift+M` share a
 * code, so a matcher that ignored the modifier would fire both.
 */
export function matches(event, id) {
  const s = shortcut(id);
  return event.code === s.code && event.shiftKey === Boolean(s.shift);
}

export default SHORTCUTS;
